/**
 * SISTEMA UNIFICADO DE ALMACENAMIENTO INDEXEDDB
 *
 * Este módulo centraliza TODOS los accesos a IndexedDB para evitar conflictos
 * entre el sistema de fotos, store de Zustand y Service Worker
 */

// Configuración centralizada
export interface UnifiedStorageI {
  savePhoto(key: string, blob: Blob, metadata?: any): Promise<void>;
  getPhoto(key: string): Promise<Blob | null>;
  deletePhoto(key: string): Promise<void>;
  listPhotos(): Promise<PhotoEntry[]>;
  removeSyncItem(syncId: string): Promise<void>;
  saveWorkflowData(key: string, data: any, type?: string): Promise<void>;
  getWorkflowData(key: string): Promise<any>;
  listWorkflows(): Promise<WorkflowEntry[]>;
  getStorageStats(): Promise<{
    photos: number;
    workflow: number;
    totalSize: number;
    byStore: Record<string, number>;
  }>;
  cleanupOldData(daysOld?: number): Promise<number>;
  repairDatabase(): Promise<boolean>;
  close(): Promise<void>;
  listSyncQueue(): Promise<SyncQueueEntry[]>; // Añadido aquí
}

function detectWorkflowType(storeName: string): string {
  // Normalizar el nombre para análisis

  // Patrones mejorados para tu nueva nomenclatura
  const patterns = [
    // ===== CONTENEDORES FULL =====
    {
      pattern: /workflow-container.*-full-puce-\d+$/i,
      type: "workflow-container-full",
      description: "Container Full con índice numérico",
    },
    {
      pattern: /workflow-container\d*-full-puce$/i,
      type: "workflow-container-full",
      description: "Container Full sin índice",
    },

    // ===== CONTENEDORES EMPTY =====
    {
      pattern: /workflow-container.*-empty-puce-\d+$/i,
      type: "workflow-container-empty",
      description: "Container Empty con índice numérico",
    },
    {
      pattern: /workflow-container.*-empty-puce$/i,
      type: "workflow-container-empty",
      description: "Container Empty sin índice",
    },

    // ===== CHECKPOINTS =====
    {
      pattern: /workflow.*checkpoint.*puce-\d+$/i,
      type: "workflow-checkpoint",
      description: "Checkpoint con índice numérico",
    },
    {
      pattern: /workflow.*checkpoint.*puce$/i,
      type: "workflow-checkpoint",
      description: "Checkpoint sin índice",
    },

    // ===== TRUCKS =====
    {
      pattern: /workflow.*truck.*puce-\d+$/i,
      type: "workflow-truck",
      description: "Truck con índice numérico",
    },
    {
      pattern: /workflow.*truck.*puce$/i,
      type: "workflow-truck",
      description: "Truck sin índice",
    },

    // ===== LOADING =====
    {
      pattern: /workflow.*loading.*puce-\d+$/i,
      type: "workflow-loading",
      description: "Loading con índice numérico",
    },
    {
      pattern: /workflow.*loading.*puce$/i,
      type: "workflow-loading",
      description: "Loading sin índice",
    },

    // ===== INSPECTION =====
    {
      pattern: /workflow.*inspection.*puce-\d+$/i,
      type: "workflow-inspection",
      description: "Inspection con índice numérico",
    },
    {
      pattern: /workflow.*inspection.*puce$/i,
      type: "workflow-inspection",
      description: "Inspection sin índice",
    },

    // ===== PATRONES GENÉRICOS (fallback) =====
    {
      pattern: /container.*full/i,
      type: "workflow-container-full",
      description: "Container Full genérico",
    },
    {
      pattern: /container.*empty/i,
      type: "workflow-container-empty",
      description: "Container Empty genérico",
    },
    {
      pattern: /truck/i,
      type: "workflow-truck",
      description: "Truck genérico",
    },
    {
      pattern: /checkpoint/i,
      type: "workflow-checkpoint",
      description: "Checkpoint genérico",
    },
  ];

  // Buscar patrón que coincida (orden importa - más específicos primero)
  for (const { pattern, type, description } of patterns) {
    if (pattern.test(storeName)) {
      // Solo log en desarrollo para debugging
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[detectWorkflowType] ${storeName} → ${type} (${description})`
        );
      }
      return type;
    }
  }

  // Log de warning para nombres no reconocidos
  console.warn(
    `[detectWorkflowType] Patrón no reconocido: ${storeName}, usando tipo por defecto`
  );

  // Tipo por defecto
  return "workflow-general";
}

function extractWorkflowIndex(storeName: string): string | null {
  const match = storeName.match(/-(\d+)$/);
  return match ? match[1] : null;
}

const CONFIG = {
  DB_NAME: "puce-unified-storage-mobile",
  DB_VERSION: 1,

  STORES: {
    PHOTOS: "photos-store",
    WORKFLOW_DATA: "workflow-store",
    CACHE_DATA: "cache-store",
    SYNC_QUEUE: "sync-queue",
    CONFIG: "config-store",
  },
} as const;

// Interfaces
interface PhotoEntry {
  id: string;
  blob: Blob;
  timestamp: number;
  metadata?: any;
}

interface WorkflowEntry {
  id: string;
  data: any;
  timestamp: number;
  type: string;
}

interface SyncQueueEntry {
  id: string;
  type: "photo" | "workflow";
  url: string;
  method: string;
  headers: Record<string, string>;
  body: any;
  timestamp: number;
  attempts: number;
  status: "pending" | "processing" | "failed";
}

// Clase principal del gestor unificado
class UnifiedStorageManager {
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;
  private isInitializing = false;

  constructor() {
    // console.log("UnifiedStorageManager creado - esperando SW");
    console.log("[Unified Storage Manager]: Accediendo al constructor");
    this.ensureDB();
  }

  /**
   * Garantiza que la base de datos esté disponible
   */
  private async ensureDB(): Promise<IDBDatabase> {
    if (this.db && this.db.objectStoreNames.length > 0) {
      return this.db;
    }

    if (this.dbPromise) {
      return this.dbPromise;
    }

    if (this.isInitializing) {
      // Esperar con backoff exponencial
      let attempts = 0;
      while (this.isInitializing && attempts < 10) {
        await new Promise((resolve) =>
          setTimeout(resolve, 100 * Math.pow(2, attempts))
        );
        attempts++;
      }

      if (this.db) return this.db;
      if (this.dbPromise) return this.dbPromise;
    }

    this.isInitializing = true;

    this.dbPromise = new Promise((resolve, reject) => {
      // Cerrar conexión existente si la hay
      if (this.db) {
        this.db.close();
        this.db = null;
      }

      const request = indexedDB.open(CONFIG.DB_NAME, CONFIG.DB_VERSION);

      request.onerror = () => {
        console.error("Error abriendo base de datos unificada:", request.error);
        this.isInitializing = false;
        reject(request.error);
      };

      request.onblocked = () => {
        console.warn("Apertura de DB bloqueada - cerrando otras conexiones");
        // Notificar a otras pestañas
        if (typeof BroadcastChannel !== "undefined") {
          const channel = new BroadcastChannel("db-management");
          channel.postMessage({
            type: "CLOSE_DB_CONNECTIONS",
            dbName: CONFIG.DB_NAME,
          });
          channel.close();
        }
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        // console.log("Creando/actualizando estructura de DB unificada");

        // Crear todos los stores necesarios
        Object.values(CONFIG.STORES).forEach((storeName) => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: "id" });

            // Índices comunes
            store.createIndex("timestamp", "timestamp", { unique: false });

            if (storeName === CONFIG.STORES.SYNC_QUEUE) {
              store.createIndex("type", "type", { unique: false });
              store.createIndex("status", "status", { unique: false });
            }

            // console.log(`Store creado: ${storeName}`);
          }
        });
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitializing = false;

        // Manejar cierre inesperado
        this.db.onclose = () => {
          console.warn("Conexión a DB cerrada inesperadamente");
          this.db = null;
          this.dbPromise = null;
        };

        // console.log("Base de datos unificada inicializada correctamente");
        resolve(this.db);
      };
    });

    return this.dbPromise;
  }

  /**
   * Ejecuta una transacción de forma segura
   */
  private async executeTransaction<T>(
    storeNames: string | string[],
    mode: IDBTransactionMode,
    callback: (stores: IDBObjectStore | IDBObjectStore[]) => Promise<T>
  ): Promise<T> {
    const db = await this.ensureDB();

    return new Promise((resolve, reject) => {
      // ✅ AÑADIR: Timeout de seguridad
      const timeout = setTimeout(() => {
        reject(new Error("Transaction timeout after 30 seconds"));
      }, 30000);

      try {
        const transaction = db.transaction(storeNames, mode);

        transaction.onerror = () => {
          clearTimeout(timeout);
          console.error("Error en transacción:", transaction.error);
          reject(transaction.error);
        };

        // ✅ AÑADIR: Manejo de abort
        transaction.onabort = () => {
          clearTimeout(timeout);
          reject(new Error("Transaction was aborted"));
        };

        transaction.oncomplete = () => {
          clearTimeout(timeout);
        };

        const stores = Array.isArray(storeNames)
          ? storeNames.map((name) => transaction.objectStore(name))
          : transaction.objectStore(storeNames);

        callback(stores)
          .then((result) => {
            clearTimeout(timeout);
            resolve(result);
          })
          .catch((error) => {
            clearTimeout(timeout);
            reject(error);
          });
      } catch (error) {
        clearTimeout(timeout);
        console.error("Error ejecutando transacción:", error);
        reject(error);
      }
    });
  }

  // ============================================
  // OPERACIONES PARA FOTOS
  // ============================================

  /**
   * Guarda una foto (reemplaza saveImageBlob)
   */
  async savePhoto(key: string, blob: Blob, metadata?: any): Promise<void> {
    const entry: PhotoEntry = {
      id: key,
      blob,
      timestamp: Date.now(),
      metadata,
    };

    await this.executeTransaction(
      CONFIG.STORES.PHOTOS,
      "readwrite",
      async (store) => {
        return new Promise<void>((resolve, reject) => {
          const request = (store as IDBObjectStore).put(entry);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
    );

    // console.log(`Foto guardada: ${key}`);
  }

  /**
   * Obtiene una foto (reemplaza getImageBlob)
   */
  async getPhoto(key: string): Promise<Blob | null> {
    try {
      const entry = await this.executeTransaction(
        CONFIG.STORES.PHOTOS,
        "readonly",
        async (store) => {
          return new Promise<PhotoEntry | null>((resolve, reject) => {
            const request = (store as IDBObjectStore).get(key);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
          });
        }
      );

      return entry ? entry.blob : null;
    } catch (error) {
      console.error(`Error obteniendo foto ${key}:`, error);
      return null;
    }
  }

  /**
   * Elimina una foto (reemplaza removeImageBlob)
   */
  async deletePhoto(key: string): Promise<void> {
    await this.executeTransaction(
      CONFIG.STORES.PHOTOS,
      "readwrite",
      async (store) => {
        return new Promise<void>((resolve, reject) => {
          const request = (store as IDBObjectStore).delete(key);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
    );

    // console.log(`Foto eliminada: ${key}`);
  }

  /**
   * Lista todas las fotos
   */
  async listPhotos(): Promise<PhotoEntry[]> {
    return this.executeTransaction(
      CONFIG.STORES.PHOTOS,
      "readonly",
      async (store) => {
        return new Promise<PhotoEntry[]>((resolve, reject) => {
          const request = (store as IDBObjectStore).getAll();
          request.onsuccess = () => resolve(request.result || []);
          request.onerror = () => reject(request.error);
        });
      }
    );
  }

  async listWorkflows(): Promise<WorkflowEntry[]> {
    // 1. Listar desde workflow-store
    const workflowsFromStore = await this.executeTransaction(
      CONFIG.STORES.WORKFLOW_DATA,
      "readonly",
      async (store) => {
        return new Promise<WorkflowEntry[]>((resolve, reject) => {
          const request = (store as IDBObjectStore).getAll();
          request.onsuccess = () => resolve(request.result || []);
          request.onerror = () => reject(request.error);
        });
      }
    );

    // 2. Listar almacenes Zustand conocidos
    const knownStores = [
      "workflow-container-full-puce-0",
      "workflow-container-full-puce-1",
      "workflow-container-full-puce-2",
      "workflow-container-full-puce-3",
      "workflow-container-empty-puce-0",
      "workflow-container-empty-puce-1",
      "workflow-container-empty-puce-2",
      "workflow-container-empty-puce-3",
      "workflow-container-checkpoint-puce-0",
      "workflow-container-checkpoint-puce-1",
      "workflow-container-checkpoint-puce-2",
      "workflow-container-checkpoint-puce-3",
      "workflow-truck-puce",
    ];

    const workflowsFromZustand: WorkflowEntry[] = [];
    for (const storeName of knownStores) {
      try {
        const data = await this.getWorkflowData(storeName);
        if (data && data.workflows) {
          Object.entries(data.workflows).forEach(
            ([index, workflowData]: [string, any]) => {
              workflowsFromZustand.push({
                id: `${storeName}-${index}`,
                data: workflowData,
                type: detectWorkflowType(storeName),
                timestamp: workflowData._metadata?.lastSaved
                  ? new Date(workflowData._metadata.lastSaved).getTime()
                  : Date.now(),
              });
            }
          );
        }
      } catch (error) {
        console.warn(`Error leyendo almacén Zustand ${storeName}:`, error);
      }
    }

    const allWorkflows = [...workflowsFromStore];
    for (const workflow of workflowsFromZustand) {
      const index = allWorkflows.findIndex((w) => w.id === workflow.id);
      if (index >= 0) {
        allWorkflows[index] = workflow;
      } else {
        allWorkflows.push(workflow);
      }
    }

    return allWorkflows;
  }

  async listSyncQueue(): Promise<SyncQueueEntry[]> {
    return this.executeTransaction(
      CONFIG.STORES.SYNC_QUEUE,
      "readonly",
      async (store) => {
        return new Promise<SyncQueueEntry[]>((resolve, reject) => {
          const request = (store as IDBObjectStore).getAll();
          request.onsuccess = () => resolve(request.result || []);
          request.onerror = () => reject(request.error);
        });
      }
    );
  }

  async removeSyncItem(syncId: string): Promise<void> {
    await this.executeTransaction(
      CONFIG.STORES.SYNC_QUEUE,
      "readwrite",
      async (store) => {
        return new Promise<void>((resolve, reject) => {
          const request = (store as IDBObjectStore).delete(syncId);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
    );
  }

  // ============================================
  // OPERACIONES PARA DATOS DE WORKFLOW
  // ============================================

  /**
   * Guarda datos de workflow (para Zustand persistence)
   */
  async saveWorkflowData(
    key: string,
    data: any,
    type: string = "workflow"
  ): Promise<void> {
    const entry: WorkflowEntry = {
      id: key,
      data,
      type,
      timestamp: Date.now(),
    };

    await this.executeTransaction(
      CONFIG.STORES.WORKFLOW_DATA,
      "readwrite",
      async (store) => {
        return new Promise<void>((resolve, reject) => {
          const request = (store as IDBObjectStore).put(entry);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
    );
  }

  /**
   * Obtiene datos de workflow
   */
  async getWorkflowData(key: string): Promise<any> {
    const entry = await this.executeTransaction(
      CONFIG.STORES.WORKFLOW_DATA,
      "readonly",
      async (store) => {
        return new Promise<WorkflowEntry | null>((resolve, reject) => {
          const request = (store as IDBObjectStore).get(key);
          request.onsuccess = () => resolve(request.result || null);
          request.onerror = () => reject(request.error);
        });
      }
    );

    return entry ? entry.data : null;
  }

  // ============================================
  // OPERACIONES DE DIAGNÓSTICO Y LIMPIEZA
  // ============================================

  /**
   * Obtiene estadísticas de uso
   */
  async getStorageStats(): Promise<{
    photos: number;
    workflow: number;
    totalSize: number;
    byStore: Record<string, number>;
  }> {
    const stats = {
      photos: 0,
      workflow: 0,
      totalSize: 0,
      byStore: {} as Record<string, number>,
    };

    // Contar elementos por store
    for (const [storeKey, storeName] of Object.entries(CONFIG.STORES)) {
      try {
        const count = await this.executeTransaction(
          storeName,
          "readonly",
          async (store) => {
            return new Promise<number>((resolve, reject) => {
              const request = (store as IDBObjectStore).count();
              request.onsuccess = () => resolve(request.result);
              request.onerror = () => reject(request.error);
            });
          }
        );

        stats.byStore[storeName] = count;

        if (storeName === CONFIG.STORES.PHOTOS) stats.photos = count;
        if (storeName === CONFIG.STORES.WORKFLOW_DATA) stats.workflow = count;
      } catch (error) {
        console.warn(`Error obteniendo stats para ${storeName}:`, error);
        stats.byStore[storeName] = 0;
      }
    }

    return stats;
  }

  /**
   * Limpia datos antiguos (más de X días)
   */
  async cleanupOldData(daysOld: number = 30): Promise<number> {
    const cutoffTime = Date.now() - daysOld * 24 * 60 * 60 * 1000;
    let deletedCount = 0;

    for (const storeName of Object.values(CONFIG.STORES)) {
      try {
        const deleted = await this.executeTransaction(
          storeName,
          "readwrite",
          async (store) => {
            return new Promise<number>((resolve, reject) => {
              let count = 0;
              const index = (store as IDBObjectStore).index("timestamp");
              const request = index.openCursor(
                IDBKeyRange.upperBound(cutoffTime)
              );

              request.onsuccess = () => {
                const cursor = request.result;
                if (cursor) {
                  cursor.delete();
                  count++;
                  cursor.continue();
                } else {
                  resolve(count);
                }
              };

              request.onerror = () => reject(request.error);
            });
          }
        );

        deletedCount += deleted;
        if (deleted > 0) {
          // console.log(
          //   `Limpiados ${deleted} elementos antiguos de ${storeName}`
          // );
        }
      } catch (error) {
        console.warn(`Error limpiando ${storeName}:`, error);
      }
    }

    return deletedCount;
  }

  /**
   * Repara la base de datos (en caso de corrupción)
   */
  async repairDatabase(): Promise<boolean> {
    try {
      console.log("Iniciando reparación de base de datos...");

      // Cerrar conexión actual
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      this.dbPromise = null;

      // Eliminar DB corrupta
      await new Promise<void>((resolve, reject) => {
        const deleteRequest = indexedDB.deleteDatabase(CONFIG.DB_NAME);
        deleteRequest.onsuccess = () => resolve();
        deleteRequest.onerror = () => reject(deleteRequest.error);
        deleteRequest.onblocked = () => {
          console.warn("Eliminación bloqueada, esperando...");
          setTimeout(resolve, 2000);
        };
      });

      // Recrear DB
      await this.ensureDB();

      console.log("Base de datos reparada exitosamente");
      return true;
    } catch (error) {
      console.error("Error reparando base de datos:", error);
      return false;
    }
  }

  /**
   * Cierra la conexión de manera segura
   */
  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.dbPromise = null;
  }
}

// Instancia singleton
export const unifiedStorage = new UnifiedStorageManager() as UnifiedStorageI;

// ============================================
// FUNCIONES DE COMPATIBILIDAD
// ============================================

/**
 * Funciones que mantienen compatibilidad con el código existente
 */

export const saveImageBlob = async (key: string, blob: Blob): Promise<void> => {
  await unifiedStorage.savePhoto(key, blob);
};

export const getImageBlob = async (key: string): Promise<Blob | undefined> => {
  const blob = await unifiedStorage.getPhoto(key);
  return blob || undefined;
};

export const removeImageBlob = async (key: string): Promise<void> => {
  await unifiedStorage.deletePhoto(key);
};

// Para el storage de Zustand
export const unifiedZustandStorage = {
  getItem: async (name: string): Promise<string | null> => {
    try {
      const data = await unifiedStorage.getWorkflowData(name);
      const ladata = data ? JSON.stringify(data) : null;
      console.log(ladata)
      return ladata;
    } catch (error) {
      console.warn(`[UNIFIED] Error obteniendo ${name}:`, error);
      return null;
    }
  },
  setItem: async (name: string, value: string): Promise<void> => {
    try {
      console.log("🔵 [SETITEM] Guardando:", {
        name,
        workflowType: detectWorkflowType(name),
        workflowIndex: extractWorkflowIndex(name),
        dataSize: value.length,
      });

      const data = JSON.parse(value);
      const workflowType = detectWorkflowType(name);
      const workflowIndex = extractWorkflowIndex(name);

      // ✅ VERIFICACIÓN: Asegurar que las keys no colisionen
      if (name.includes("empty") && workflowType !== "empty") {
        console.error(
          "❌ CONFLICTO: Key 'empty' pero tipo detectado:",
          workflowType
        );
      }
      if (name.includes("full") && workflowType !== "full") {
        console.error(
          "❌ CONFLICTO: Key 'full' pero tipo detectado:",
          workflowType
        );
      }

      const metadata = {
        type: workflowType,
        index: workflowIndex,
        storeName: name, // ← Esto debe ser único
        lastSaved: new Date().toISOString(),
      };

      await unifiedStorage.saveWorkflowData(
        name, // ← Esta debe ser la key exacta
        { ...data, _metadata: metadata },
        workflowType
      );

      console.log("✅ [SETITEM] Guardado exitoso");
    } catch (error: any) {
      console.error("❌ [SETITEM] Error:", error);
    }
  },

  removeItem: async (name: string): Promise<void> => {
    try {
      await unifiedStorage.saveWorkflowData(name, null);
      const workflowIndex = extractWorkflowIndex(name);
      const indexInfo = workflowIndex ? ` [${workflowIndex}]` : "";
    } catch (error) {
      console.error(`[UNIFIED] Error eliminando ${name}:`, error);
    }
  },
};

export const getWorkflowStats = async (): Promise<{
  totalWorkflows: number;
  byType: Record<string, { count: number; indexes: string[] }>;
  lastUpdated: Record<string, number>;
  storageHealth: "healthy" | "warning" | "error";
}> => {
  try {
    const stats = await unifiedStorage.getStorageStats();
    const errors = JSON.parse(
      localStorage.getItem("unifiedStorageErrors") || "[]"
    );

    // Obtener todos los workflows
    const workflows = await unifiedStorage.listWorkflows();

    // Agrupar por tipo
    const byType: Record<string, { count: number; indexes: string[] }> = {};
    const lastUpdated: Record<string, number> = {};

    for (const workflow of workflows) {
      const type = workflow.type || detectWorkflowType(workflow.id);
      const index = extractWorkflowIndex(workflow.id) || "";

      if (!byType[type]) {
        byType[type] = { count: 0, indexes: [] };
      }
      byType[type].count += 1;
      if (index && !byType[type].indexes.includes(index)) {
        byType[type].indexes.push(index);
      }

      lastUpdated[workflow.id] = workflow.timestamp;
    }

    // Determinar salud del almacenamiento
    let storageHealth: "healthy" | "warning" | "error" = "healthy";
    if (errors.length > 10) storageHealth = "error";
    else if (errors.length > 5) storageHealth = "warning";

    return {
      totalWorkflows: stats.workflow,
      byType,
      lastUpdated,
      storageHealth,
    };
  } catch (error) {
    return {
      totalWorkflows: 0,
      byType: {},
      lastUpdated: {},
      storageHealth: "error",
    };
  }
};

/**
 * Función utilitaria para listar todos los workflows de un tipo específico
 */
export const listWorkflowsByType = async (
  type: "full" | "empty" | "truck" | "checkpoint" | "loading" | "inspection"
): Promise<string[]> => {
  try {
    const workflows = await unifiedStorage.listWorkflows();
    const typeMap: Record<string, string> = {
      full: "workflow-container-full",
      empty: "workflow-container-empty",
      truck: "workflow-truck",
      checkpoint: "workflow-checkpoint",
      loading: "workflow-loading",
      inspection: "workflow-inspection",
    };

    const targetType = typeMap[type];
    if (!targetType) {
      throw new Error(`Tipo de workflow no soportado: ${type}`);
    }

    return workflows
      .filter(
        (workflow) =>
          (workflow.type || detectWorkflowType(workflow.id)) === targetType
      )
      .map((workflow) => workflow.id);
  } catch (error) {
    console.error(`Error listando workflows tipo ${type}:`, error);
    return [];
  }
};

/**
 * Función para limpiar workflows antiguos o corruptos
 */
export const cleanupWorkflows = async (
  olderThanDays: number = 30,
  typeRetentionRules?: Partial<
    Record<
      "full" | "empty" | "truck" | "checkpoint" | "loading" | "inspection",
      number
    >
  >
): Promise<{
  cleaned: number;
  errors: number;
}> => {
  try {
    const workflows = await unifiedStorage.listWorkflows();
    let cleaned = 0;
    let errors = 0;

    const typeMap: Record<string, string> = {
      full: "workflow-container-full",
      empty: "workflow-container-empty",
      truck: "workflow-truck",
      checkpoint: "workflow-checkpoint",
      loading: "workflow-loading",
      inspection: "workflow-inspection",
    };

    for (const workflow of workflows) {
      const type = workflow.type || detectWorkflowType(workflow.id);
      const simpleType = Object.keys(typeMap).find(
        (key) => typeMap[key] === type
      ) as keyof typeof typeMap | undefined;
      let daysOld = olderThanDays;
      if (
        simpleType &&
        typeRetentionRules &&
        simpleType in typeRetentionRules
      ) {
        daysOld = (typeRetentionRules as any)[simpleType]!;
      }

      const cutoffTime = Date.now() - daysOld * 24 * 60 * 60 * 1000;

      if (workflow.timestamp < cutoffTime) {
        try {
          await unifiedStorage.saveWorkflowData(workflow.id, null, type);
          cleaned += 1;
          // console.log(`Eliminado workflow antiguo: ${workflow.id} (${type})`);
        } catch (error) {
          console.warn(`Error eliminando workflow ${workflow.id}:`, error);
          errors += 1;
        }
      }
    }

    // console.log(
    //   `[UNIFIED] Limpieza completada: ${cleaned} workflows eliminados, ${errors} errores`
    // );

    return { cleaned, errors };
  } catch (error) {
    console.error("Error en limpieza de workflows:", error);
    return { cleaned: 0, errors: 1 };
  }
};

export default unifiedStorage;
